/**
 * Where a founder goes to submit a deal.
 *
 * Canon decision `D-008` puts submission behind a Google sign-in, and canon § 14 makes that
 * session the only way a founder is known. A public page cannot hold a session and must not
 * try — so this site stopped hosting the form (`WEB-003`) and became the entry point
 * `docs/ARCHITECTURE.md` § Public web now describes: it *names the destination* and hands
 * the browser over.
 *
 * The destination is the seller app's root rather than a path of its own. `dealbid-app` is
 * a single screen behind a session gate (`APP-008`): an unknown visitor is offered sign-in,
 * and a signed-in founder with no deals is offered the submission form (`APP-009`). A
 * `/submit` path invented here would be a route that repo does not have.
 *
 * The invite code rides along in the query string, because that is where `dealbid-app` reads
 * it from — `src/session/signIn.ts` there lifts it off its own address bar and forwards it to
 * `GET /v1/seller/session/start`. Canon § 5 decides the signup source at first sign-in, so a
 * code that does not survive this hop is an attribution Dealbid cannot recover later. Since
 * decision `D-011` it costs the founder nothing — the grant is banded by approval sequence,
 * not by how they arrived — but the record is still only obtainable here.
 *
 * That is not in tension with `src/lib/invite.ts` scrubbing the code from *this* site's
 * address bar. The code is scrubbed here because every board row links out to a seller's
 * destination URL, and a code sitting in `dealbid.app`'s address bar would ride into the
 * `Referer` of every one of those outbound requests. It is re-attached to exactly one link:
 * the one leaving for Dealbid's own seller app.
 */
import { appUrl } from "../config/env";
import { INVITE_QUERY_PARAM, readInviteCode } from "./invite";

/**
 * The seller app, carrying the founder invite code when this visit arrived with one.
 *
 * Read at call time rather than at module load: the code is captured from the URL during
 * startup (`captureInviteCode`), and a value frozen before that ran would drop it.
 */
export function sellerSubmitUrl(): string {
  const url = new URL(appUrl("/"));
  const code = readInviteCode();
  if (code !== null) url.searchParams.set(INVITE_QUERY_PARAM, code);
  return url.toString();
}
